import React, { useRef, PointerEvent, useContext } from 'react';
import './Audio.css';
import useTypedSelector from '../../hooks/use-type-selectors';
import useActions from '../../hooks/use-actions';
import Timeline from '../Timeline/Timeline';
import StageCanvas, { StageCanvasMode } from '../StageCanvas/StageCanvas';
import useAudioDrag, { DragType } from '../../hooks/useAudioDrag';

import { AudioPlaybackState } from '../../models/AudioPlayback';
import { ILoadedAudioFile } from '../../models/LoadedAudioFiles';
import { TimelineContainerContext } from '../../context/TimelineContainerContext';
import useRemConvert from '../../hooks/useRemConvert';
import { toTimecode } from '../../utils/general';
import { giveScreenReaderFeedback } from '../../utils/screenReader';
import { getAllAudioTracks } from '../../models/Timeline';
//import '../../App.css'

const Audio: React.FC = () => {
	const importFileInput = useRef<HTMLInputElement>(null);
	const audioFiles = useTypedSelector((state) => state.undoable.audioFiles);
	const loadedAudioFiles = useTypedSelector((state) => state.loadedAudioFiles);
	const audioPlayback = useTypedSelector((state) => state.audioPlayback);
	const audio = useTypedSelector((state) => state.audio);
	const timeline = useTypedSelector((state) => state.undoable.timeline);
	const {
		importFiles,
		playAudioFile,
		stopPlayback,
		deleteAudioFile,
		insertAudioClip,
		updateFilter,
	} = useActions();
	const { drag, setDrag } = useAudioDrag();
	const { remToPx } = useRemConvert();
	const {
		trackContentContainerRef,
		timelineScrollDivRef,
		audioTrackPositionsRef,
		timelineScale,
	} = useContext(TimelineContainerContext);
	const trackContentContainer = trackContentContainerRef?.current || null;
	const timelineScrollDiv = timelineScrollDivRef?.current || null;
	const audioTrackPositions = audioTrackPositionsRef?.current || [];

	const onPointerDown = (event: PointerEvent<HTMLDivElement>, clip: ILoadedAudioFile) => {
		if (trackContentContainer === null) return;

		event.currentTarget.setPointerCapture(event.pointerId);
		const clipRect = event.currentTarget.getBoundingClientRect();

		// No pointer offset, always grab top left corner of clip
		const pointerOffset = { x: 0, y: 0 };
		setDrag({
			type: DragType.AudioClip,
			audioClip: clip,
			audioTrack: null,
			pos: {
				x: event.clientX,
				y: event.clientY - pointerOffset.y,
			},
			pointerOffset: pointerOffset,
		});

		if (audioPlayback.state === AudioPlaybackState.PlayingTimeline) {
			stopPlayback();
		}
	};

	const onPointerMove = (event: PointerEvent<HTMLDivElement>, clipId: number) => {
		if (drag?.type !== DragType.AudioClip || drag.audioClip.id !== clipId) return;
		if (trackContentContainer === null) return;

		setDrag({
			...drag,
			pos: {
				x: Math.max(0, event.clientX),
				y: Math.max(0, event.clientY - drag.pointerOffset.y),
			},
		});
	};

	const onPointerUp = (event: PointerEvent<HTMLDivElement>, loadedFile: ILoadedAudioFile) => {
		if (drag?.type !== DragType.AudioClip || drag.audioClip.id !== loadedFile.id) return;

		if (trackContentContainer === null || timelineScrollDiv === null) {
			setDrag(null);
			return;
		}
		const trackContentContainerRect = trackContentContainer.getBoundingClientRect();

		let newTrackId = null;
		const pointerYPos = event.clientY;
		const pointerXPos = drag.pos.x;

		const timelineScrollDivRect = timelineScrollDiv.getBoundingClientRect();
		if (
			pointerYPos < timelineScrollDivRect.top ||
			pointerYPos > timelineScrollDivRect.bottom ||
			pointerXPos < timelineScrollDivRect.left ||
			pointerXPos > timelineScrollDivRect.right
		) {
			setDrag(null);
			return;
		}

		for (const audioTrackPos of audioTrackPositions) {
			if (
				pointerYPos > trackContentContainerRect.top + audioTrackPos.yMin &&
				pointerYPos < trackContentContainerRect.top + audioTrackPos.yMax &&
				pointerXPos > trackContentContainerRect.left - 10
			) {
				newTrackId = audioTrackPos.trackId;
				break;
			}
		}

		if (newTrackId !== null) {
			const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
			const time =
				Math.max(0, event.clientX - trackContentContainerRect.left) / timelineScale;
			insertAudioClip(loadedFile.id, time, loadedFile.duration, newTrackId);
		}

		setDrag(null);
	};

	let dragAudioClip = null;
	if (
		drag?.type === DragType.AudioClip &&
		trackContentContainer !== null &&
		timelineScrollDiv !== null
	) {
		let yPos = drag.pos.y;

		// Snap y pos to audio track if cursor is above one
		const pointerYPos = drag.pos.y + drag.pointerOffset.y;
		const pointerXPos = drag.pos.x;
		const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
		const timelineScrollDivRect = timelineScrollDiv.getBoundingClientRect();
		if (
			pointerYPos > timelineScrollDivRect.top &&
			pointerYPos < timelineScrollDivRect.bottom &&
			pointerXPos > timelineScrollDivRect.left &&
			pointerXPos < timelineScrollDivRect.right
		) {
			for (const audioTrackPos of audioTrackPositions) {
				if (
					pointerYPos > trackContentContainerRect.top + audioTrackPos.yMin &&
					pointerYPos < trackContentContainerRect.top + audioTrackPos.yMax
				) {
					yPos = trackContentContainerRect.top + audioTrackPos.yMax - remToPx(1.7) + 5;
					break;
				}
			}
		}

		dragAudioClip = (
			<div
				className='timelineAudioClip timelineAudioClipDrag'
				key={drag.audioClip.id}
				style={{
					position: 'fixed',
					width: drag.audioClip.duration * timelineScale,
					height: remToPx(1.7),
					left: drag.pos.x,
					top: yPos,
					color: 'white',
					zIndex: 2,
				}}
			>
				{audioFiles.files.find((file) => file.id === drag.audioClip.id)?.name}
			</div>
		);
	}

	function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
		let files = e.target.files;
		if (files === null) return;
		importFiles(Array.from(files));
		e.target.value = '';
		//giveScreenReaderFeedback([FILE NAME] + " inserted"); <-- needs the file name
	}
	
	function filterAudioFile(filterText: String, fileName: String) {
		return filterText
			.toLowerCase()
			.split(/[&,/]/)
			.some((subFilter) =>
				fileName.toLowerCase().includes(subFilter)
			);
	}

	function filterOnChange(e: React.ChangeEvent<HTMLInputElement>) {
		let filterText = e.target.value;
		
		updateFilter(filterText);

		let results = audioFiles.files.filter((file) => filterAudioFile(filterText, file.name)).length;
		giveScreenReaderFeedback(`Search complete with ${results} ${results === 1 ? "result" : "results"}.`, true);
	}

	function clearFilter() {
		updateFilter('');
		giveScreenReaderFeedback('cleared');
	}

	return (
		<div className='gridContainer'>
			<div className='sideBar'>
				<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					<div style={{ display: 'flex' }}>
						<img className='audioIcon' alt='audio' aria-hidden/>
						<h1 style={{ margin: '0px' }}>AUDIO</h1>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<button
								aria-label='import audio clips'
								aria-controls='feedback'
								className='importButton'
								onClick={() => importFileInput.current?.click()}
							/>
							<input
								ref={importFileInput}
								type='file'
								multiple
								onChange={onFileInputChange}
								style={{ display: 'none' }}
							></input>
						</div>
					</div>
					<div className='searchBarContainer'>
						<div className='searchBarIcon' />
						<input
							aria-label={
								'filter terms for audio clips. ' /* + browser.filter.toLowerCase().replaceAll(/[&,/]/g, ' ')*/
							}
							className='searchBarInput'
							type='text'
							id='myInput'
							value={audio.filter}
							onChange={filterOnChange}
						/>
						<button
							aria-label='clear filter terms'
							className='searchBarClearButton'
							onClick={clearFilter}
						/>
					</div>
					<div
						style={{
							backgroundColor: '#191919',
							marginTop: '20px',
							width: '100%',
							overflow: 'auto',
							flexGrow: 1,
						}}
					>
						{audioFiles.files
							.filter((file) => filterAudioFile(audio.filter, file.name))
							.map((file) => {
								const loadedFile = loadedAudioFiles.files.find(
									(loadedFile) => loadedFile.id === file.id
								);
								if (loadedFile !== undefined) {
									return (
										<div key={file.id} className='songEntryContainer'>
											<div
												className='songEntryTitle'
												style={{ cursor: 'move' }}
												onPointerDown={(e) => onPointerDown(e, loadedFile)}
												onPointerMove={(e) =>
													onPointerMove(e, loadedFile.id)
												}
												onPointerUp={(e) => onPointerUp(e, loadedFile)}
											>
												{file.name}
											</div>
											<div className='songEntryDuration'>
												{toTimecode(loadedFile.duration)}
											</div>
											<div
												style={{
													display: 'flex',
													justifyContent: 'flex-end',
												}}
											>
												{audioPlayback.audioFileId !== file.id ? (
													<button
														aria-label={`play ` + file.name}
														className='songActionButton-Play'
														onClick={() => {
															playAudioFile(file.id);
															giveScreenReaderFeedback(`playing`);
														}}
													></button>
												) : (
													<button
														aria-label={`stop ` + file.name}
														className='songActionButton-Stop'
														onClick={() => {
															stopPlayback();
															giveScreenReaderFeedback(`stopped`);
														}}
													></button>
												)}
												<button
													aria-label={
														`insert ` + file.name + ` to timeline`
													}
													className='songInsertButton'
													onClick={() => {
														if (getAllAudioTracks(timeline).some((audioTrack) => !audioTrack.muted)) {
															insertAudioClip(
																file.id,
																audioPlayback.timelineTime,
																loadedFile.duration || 0
															);
															giveScreenReaderFeedback(`${file.name} has been inserted on the timeline`, true);
														} else {
															giveScreenReaderFeedback(`Could not insert ${file.name} because all tracks are off`, true);
														}
													}}
												>
													Insert
												</button>
												<button
													aria-label={`remove ` + file.name}
													className='songActionButton-Delete'
													onClick={() => {
														deleteAudioFile(file.id);
														giveScreenReaderFeedback(`removed`);
													}}
												></button>
											</div>
											<br />
										</div>
									);
								} else {
									return (
										<div key={file.id}>
											<div className='songEntryTitle'>{file.name}</div>
											<div className='songEntryTitle'>Loading...</div>
											<br />
										</div>
									);
								}
							})}
					</div>
				</div>
			</div>
			<div className='stage'>
				<StageCanvas mode={StageCanvasMode.ViewOnly} />
			</div>
			<div className='timeline'>
				<Timeline />
			</div>
			{dragAudioClip}
		</div>
	);
};

export default Audio;
